import dayjs from "dayjs";
import React from "react";
import {
  ChartComponentProps,
  ChartConfig,
  createChartConfig,
  AssetDataPoint,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import { AssetsTraded } from "src/components/Referral-partner/Analytics/charts/AssetsTraded"; // Update import
import { WarningWrapper } from "src/components/Referral-partner/Analytics/styles/shared.styles";
import api from "src/store/features/api";

const useGetAssetsTradedConfig = (): { assetsTraded: ChartConfig<AssetDataPoint> } => {
  // fetch data and states
  const {
    data: rawAssetsTraded,
    isLoading: assetsIsLoading,
    isFetching: assetsIsFetching,
    isError: assetsIsError,
    error: assetsError,
  } = api.useGetAssetsTradedQuery({
    timeFrame: "1Y",
    timezone: dayjs.tz.guess(),
  });

  // format data - no filtering needed as we want to show all months
  const assetsData = React.useMemo((): AssetDataPoint[] => {
    if (!rawAssetsTraded) return [];
    return rawAssetsTraded.data.map(
      (month): AssetDataPoint => ({
        timestamp: month.timestamp,
        totalValue: month.totalValue || 0,
        topAssets: month.topAssets.map((asset) => ({
          name: asset.name,
          percentage: asset.percentage,
          value: asset.value || 0, // Provide a default value if it's missing
        })),
      }),
    );
  }, [rawAssetsTraded]);

  // create chart config
  const assetsTradedConfig = React.useMemo(
    (): { assetsTraded: ChartConfig<AssetDataPoint> } => ({
      assetsTraded: createChartConfig<AssetDataPoint>({
        title: "Assets Traded by Month (%)",
        type: "bar", // Change to bar chart type
        menuMode: "normal",
        defaultDate: "1 year",
        dateOptions: [
          {
            label: "1 year",
            value: "1Y",
          },
        ],
        mainChart: false,
        isLoading: assetsIsLoading || assetsIsFetching,
        data: assetsData,
        component: ({ data, title }: ChartComponentProps<AssetDataPoint>): JSX.Element => {
          if (assetsIsError) {
            return <WarningWrapper>{assetsError?.message}</WarningWrapper>;
          } else if (data.length <= 0) {
            return <WarningWrapper color="#002035">No Data</WarningWrapper>;
          } else {
            return <AssetsTraded data={data} />;
          }
        },
      }),
    }),
    [assetsData, assetsIsLoading, assetsIsFetching, assetsIsError, assetsError],
  );

  return assetsTradedConfig;
};

export default useGetAssetsTradedConfig;
